import { InputAdornment, Stack } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { activityStateEnum, reportStateEnum, stepModeEnum } from '../../../../constants';
import { useCreateActivityMutation, useUpdateActivityMutation } from '../../../../hooks/mutations/useActivityMutation';
import { useUpdateReportMutation } from '../../../../hooks/mutations/useReportMutation';
import { useActivitiesTypesQuery } from '../../../../hooks/queries/useActivitiesTypesQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useUtilityVehiclesQuery } from '../../../../hooks/queries/useUtilityVehiclesQuery';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import CustomTimePicker from '../../../ui/CustomTimePicker';
import DropDown, { OptionType } from '../../../ui/DropDown';
import IconButton from '../../../ui/IconButton';
import QuantityToggleGroup from '../../../ui/QuantityToggleGroup';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailyActivityModalProps extends ModalProps {
    refetch: () => void;
    activity: DataItem | null;
    siteName?: string;
    siteId: string;
    currentDate: Date;
}

const ManageValidationDailyActivityModal: React.FC<ManageValidationDailyActivityModalProps> = ({
    open,
    handleClose,
    refetch,
    activity,
    siteName,
    siteId,
    currentDate,
}) => {
    const { t } = useTranslation();
    const [currentActivityId] = useState(activity?._id || '');

    const [selectedActivityType, setSelectedActivityType] = useState<OptionType | null>(
        activity?.activityTypeId
            ? {
                  label: activity?.activityTypeId.designation || '',
                  value: activity?.activityTypeId._id || '',
              }
            : null
    );
    const [selectedUser, setSelectedUser] = useState<OptionType | null>(
        activity?.reportId.userId
            ? {
                  label: formatName(activity?.reportId.userId.firstName, activity?.reportId.userId.lastName) || '',
                  value: activity?.reportId.userId._id || '',
              }
            : null
    );
    const [selectedVehicle, setSelectedVehicle] = useState<OptionType | null>(
        activity?.reportId.vehicleId
            ? {
                  label: activity?.reportId.vehicleId.designation || '',
                  value: activity?.reportId.vehicleId._id || '',
              }
            : null
    );
    const [isLongTrip, setIsLongTrip] = useState<boolean>(activity?.reportId.isLongTrip ?? false);
    const [observations, setObservations] = useState<string>(activity?.observationsWorker || '');
    const [overtime, setOvertime] = useState<string>(activity?.reportId.extraHours?.toString() || '');
    const [selectedTime, setSelectedTime] = useState<string | null>(activity?.startTime || null);
    const [quantity, setQuantity] = useState<number>(activity?.quantity ?? 1);

    const setSpecificHour = () => {
        const date = new Date(currentDate);
        date.setHours(7, 0);
        return date.toISOString();
    };

    const { data: dataActivityTypes } = useActivitiesTypesQuery({ queries: { active: true } });
    const { data: dataUsers } = useUsersQuery({ queries: { active: true } });
    const { data: dataUtilityVehicles } = useUtilityVehiclesQuery({ queries: { active: true } });

    const { mutate: createActivity, isLoading: isCreateActivityLoading } = useCreateActivityMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const { mutate: updateActivity, isLoading: isUpdateActivityLoading } = useUpdateActivityMutation({ onSuccess: () => refetch() });
    const { mutate: updateReport, isLoading: isUpdateReportLoading } = useUpdateReportMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postActivity = () => {
        if (!selectedActivityType || !selectedUser) return;
        createActivity(
            {
                activityTypeId: selectedActivityType.value,
                userId: selectedUser.value,
                observationsWorker: observations,
                quantity,
                startTime: setSpecificHour(),
                siteId,
                activityState: activityStateEnum.VALIDATION,
                reportState: reportStateEnum.REPORTFILLED,
                stepMode: stepModeEnum.VALIDATE,
            },
            {
                onSuccess: (data) => {
                    updateReport({
                        id: data.data.reportId || '',
                        body: {
                            isLongTrip,
                            extraHours: parseFloat(overtime) || 0,
                            vehicleId: selectedVehicle?.value || null,
                        },
                    });
                },
            }
        );
    };

    const patchActivity = () => {
        if (currentActivityId) {
            updateActivity(
                {
                    id: currentActivityId,
                    body: {
                        activityTypeId: selectedActivityType?.value,
                        observationsWorker: observations,
                        quantity,
                    },
                },
                {
                    onSettled: () => {
                        updateReport({
                            id: activity?.reportId._id || '',
                            body: {
                                isLongTrip,
                                extraHours: overtime.length && parseFloat(overtime) ? parseFloat(overtime) : 0,
                                vehicleId: selectedVehicle?.value || null,
                            },
                        });
                    },
                }
            );
        }
    };

    return (
        <ModalBody
            title={t('activity')}
            subtitle={!!currentActivityId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            closeOnSubmit={false}
            loading={isCreateActivityLoading || isUpdateActivityLoading || isUpdateReportLoading}
            onSubmit={() => {
                !currentActivityId.length ? postActivity() : patchActivity();
            }}
            disableSubmit={!selectedActivityType?.value.length || !selectedUser?.value.length || !selectedTime}
        >
            <Stack spacing={2}>
                <DropDown
                    selectedItem={selectedUser}
                    items={
                        dataUsers?.map((user) => ({
                            label: formatName(user.firstName, user.lastName),
                            value: user._id,
                        })) || []
                    }
                    handleChange={(event, value) => setSelectedUser(value)}
                    inputLabel={t('user')}
                    required
                    disabled={!!currentActivityId.length}
                />
                <Stack spacing={0.5}>
                    <CustomTimePicker
                        value={selectedTime}
                        label={capitalizeFirstLetter(t('startTime'))}
                        onChange={(value) => {
                            setSelectedTime(value);
                        }}
                        referenceDate={dayjs(currentDate.toISOString())}
                        disabled={!!currentActivityId.length}
                        required
                    />

                    <DropDown
                        selectedItem={selectedActivityType}
                        items={
                            dataActivityTypes
                                ?.filter((activityType) => !currentActivityId.length || activityType.type === activity?.activityTypeId.type)
                                .map((activityType) => ({
                                    label: activityType.designation,
                                    value: activityType._id,
                                })) || []
                        }
                        handleChange={(event, value) => setSelectedActivityType(value)}
                        inputLabel={t('activityType')}
                        required
                    />
                    <QuantityToggleGroup value={quantity} onChange={(value) => setQuantity(value)} />
                    <CustomTextInput value={observations} onChange={(value) => setObservations(value)} label={t('observations')} multiline />
                </Stack>
                <Stack spacing={0.5}>
                    <DropDown
                        selectedItem={selectedVehicle}
                        items={
                            dataUtilityVehicles?.map((vehicle) => ({
                                label: vehicle.designation,
                                value: vehicle._id,
                            })) || []
                        }
                        handleChange={(event, value) => setSelectedVehicle(value)}
                        inputLabel={t('vehicle')}
                        disableClearable={false}
                    />
                    {!dataUsers?.find((user) => user._id === selectedUser?.value)?.isSpecial && (
                        <CustomTextInput
                            label={capitalizeFirstLetter(t('overtime'))}
                            type="number"
                            value={overtime}
                            onChange={(value) => {
                                const num = parseFloat(value);
                                if (isNaN(num)) return setOvertime('');
                                const rounded = Math.round(num * 2) / 2;
                                setOvertime(rounded.toString());
                            }}
                            required
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconButton
                                        iconName="remove"
                                        disabled={parseFloat(overtime) <= -24}
                                        onClick={() => setOvertime(Math.max(-24, (parseFloat(overtime) ?? 0.5) - 0.5).toString())}
                                    />
                                </InputAdornment>
                            }
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        iconName="add"
                                        disabled={parseFloat(overtime) >= 24}
                                        onClick={() => setOvertime(Math.min(24, (parseFloat(overtime) ?? 0) + 0.5).toString())}
                                    />
                                </InputAdornment>
                            }
                        />
                    )}
                    <ToggleLine
                        checked={isLongTrip}
                        onChange={(checked) => setIsLongTrip(checked)}
                        title={capitalizeFirstLetter(t('longTrip'))}
                        disabled={dataUsers?.find((user) => user._id === selectedUser?.value)?.isSpecial}
                    />
                </Stack>
            </Stack>
        </ModalBody>
    );
};

export default ManageValidationDailyActivityModal;
